import { useEffect, useState } from 'react';
import { Copy, RefreshCcw, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useActiveOrganization,
  useActiveOrganizationId,
  useSession,
} from '@repo/api-client/react';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';

const isProd = import.meta.env.PROD && import.meta.env.MODE === 'production';

/**
 * Dev-only diagnostics. Gated to non-production builds — anyone who
 * lands here in prod sees a refusal, not the internals.
 */
export function DevToolsScreen() {
  const queryClient = useQueryClient();
  const { user } = useSession();
  const orgId = useActiveOrganizationId();
  const org = useActiveOrganization();
  const { toast } = useToast();
  const [build, setBuild] = useState<string>(import.meta.env.MODE);

  useEffect(() => {
    setBuild(import.meta.env.MODE);
  }, []);

  if (isProd) {
    return (
      <Page>
        <PageHeader title="Dev tools" />
        <Alert variant="danger">
          Dev tools are disabled in production. If you reached this URL on purpose,
          you're looking for{' '}
          <a className="font-medium text-foreground underline-offset-4 hover:underline" href="/settings/support">
            Support
          </a>
          .
        </Alert>
      </Page>
    );
  }

  function copy(label: string, value: string) {
    void navigator.clipboard?.writeText(value);
    toast({ title: `${label} copied`, variant: 'success' });
  }

  return (
    <Page>
      <PageHeader
        title="Dev tools"
        description="Internal diagnostics. Don't ship a screenshot of this to a customer."
        actions={<Badge variant="warning">Non-production only</Badge>}
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Build</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <Row label="Mode" value={build} />
            <Row label="Origin" value={typeof window !== 'undefined' ? window.location.origin : '—'} />
            <Row label="User agent" value={typeof navigator !== 'undefined' ? navigator.userAgent : '—'} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Session</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <Row label="User id" value={user?.id ?? '—'} copy={user?.id ? () => copy('User id', user.id) : undefined} />
            <Row label="Email" value={user?.email ?? '—'} />
            <Row label="Verified" value={user?.emailVerified ? 'yes' : 'no'} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Active org</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <Row label="Id" value={orgId ?? '—'} copy={orgId ? () => copy('Org id', orgId) : undefined} />
            <Row label="Name" value={org?.name ?? '—'} />
            <Row label="Currency" value={org?.currency ?? '—'} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Caches</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <p className="text-sm text-muted-foreground">
              Use these when you suspect React Query is serving stale data.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void queryClient.invalidateQueries();
                  toast({ title: 'All queries invalidated', variant: 'success' });
                }}
              >
                <RefreshCcw className="size-4" /> Invalidate all
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  queryClient.clear();
                  toast({ title: 'Query cache cleared', variant: 'success' });
                }}
              >
                <Trash2 className="size-4" /> Clear cache
              </Button>
            </div>

            <Separator className="my-3" />

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  try {
                    localStorage.clear();
                    toast({ title: 'localStorage cleared', variant: 'success' });
                  } catch {
                    toast({
                      title: 'Could not clear storage',
                      variant: 'danger',
                    });
                  }
                }}
              >
                <Trash2 className="size-4" /> Clear localStorage
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
              >
                <RefreshCcw className="size-4" /> Reload page
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Page>
  );
}

function Row({
  label,
  value,
  copy,
}: {
  label: string;
  value: string;
  copy?: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-line/60 py-1.5 last:border-b-0">
      <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1.5">
        <code className="max-w-[26ch] truncate font-mono text-xs text-foreground">{value}</code>
        {copy ? (
          <Button variant="ghost" size="icon" className="size-6" onClick={copy} aria-label={`Copy ${label}`}>
            <Copy className="size-3" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
